/**
 * Deciding whether a student is in the US.
 *
 * Three sources, weakest last:
 *
 *   1. An explicit choice, remembered. A teacher on a VPN, or one who wants the
 *      whole class on the same model regardless of where they sit, needs to be
 *      able to say so and have it stick. This always wins.
 *   2. The edge. The proxy sees the request and whatever country header the CDN
 *      attached, which is the only source here that is not a guess. The client
 *      cannot read it, so this is applied server-side and reported back.
 *   3. The browser's time zone. A guess, and a coarse one - `America/*` covers
 *      most of two continents - but it costs nothing and it is right for the
 *      common case. Used only to pick a label before the first request answers.
 *
 * Note the asymmetry that makes this safe to get wrong: the classifier only
 * ever returns an INTENT ID. Being routed to the "wrong" provider changes who
 * picked the experiment, not what the experiment computed. The numbers come
 * from the solver either way.
 */

import type { Region } from './config'

const STORAGE_KEY = 'simfab.assistant.region'

/**
 * US time zones, including the ones that do not start with `America/`. Pacific
 * and Atlantic territories are US traffic and should not fall to the
 * international default.
 */
const US_ZONES = /^(America\/(Adak|Anchorage|Boise|Chicago|Denver|Detroit|Indiana|Juneau|Kentucky|Los_Angeles|Menominee|Metlakatla|New_York|Nome|North_Dakota|Phoenix|Sitka|Yakutat)|Pacific\/(Honolulu|Guam|Midway|Pago_Pago|Saipan|Wake)|Atlantic\/(Bermuda))/

/** A remembered override, or null if the student has never chosen. */
export function storedRegion(): Region | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw === 'us' || raw === 'intl' ? raw : null
  } catch {
    // Private browsing, or storage disabled by policy. Fall through to guessing.
    return null
  }
}

export function setRegion(region: Region | null): void {
  try {
    if (region) localStorage.setItem(STORAGE_KEY, region)
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to do - the guess still works, it just will not persist.
  }
}

/** The time-zone guess, used before the proxy has told us anything better. */
export function guessRegion(): Region {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
    return US_ZONES.test(zone) ? 'us' : 'intl'
  } catch {
    return 'intl'
  }
}

/** What the client believes right now. The proxy may still correct it. */
export const currentRegion = (): Region => storedRegion() ?? guessRegion()
